import { Inject, Injectable, Optional } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AuthCheckService, ContentService, HrefResolverService } from '@blk/cwp-ng-framework';
import { AnalyticEventEvaluatorUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { MessageDialogComponent } from '@ddt-web/common';
import { ERROR_SEVERITY, IS_PUBLIC_APPLICATION } from '@ddt-web/config';
import * as get from 'lodash/get';
import { fromEvent, Subject, Subscription, timer } from 'rxjs';
import { auditTime, first, switchMap, takeUntil } from 'rxjs/operators';

import { AuthUtil } from './auth.util';
import { OktaAuthenticationService } from './okta-authentication.service';

/**
 * See https://webster.bfm.com/Wiki/display/AFWM/AC+Frontend+Workflows for more info
 */
@Injectable({
	providedIn: 'root'
})
export class SessionTimeoutService {

	/**
	 * minutes interval at which we show the timeout message - the clock resets on keep alive or http call
	 */
	private readonly OKTA_TIMEOUT_MINUTES = (8 * 60) - 5; // 8 hours - 5 minutes as a buffer

	/**
	 * @private KEEP_ALIVE_MINUTES - minutes interval at which the keep alive is issued based on mouse activity
	 */
	private readonly KEEP_ALIVE_MINUTES = 5;

	private timeoutTimer: Subject<number>;
	private timerSubscription: Subscription;

	private keepAliveSubscription: Subscription;

	constructor(
		private matDialog: MatDialog,
		private contentService: ContentService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private authService: AuthCheckService,
		private oktaAuthenticationService: OktaAuthenticationService,
		private hrefResolver: HrefResolverService,
		@Optional() @Inject(IS_PUBLIC_APPLICATION) private isPublicApp: boolean,
		@Inject('environment') private environment: any
	) {

	}

	/**
	 * This is called every time a new API is being called
	 */
	public init(): void {
		this.resetTimer();
		this.setupKeepAlive();
	}

	public forceTimeout(): void {
		console.log('[Timeout]', 'Forcing timeout');
		// Stop making any further API requests. This is used in interceptor to make requests.
		this.revertAuthenticationCheck();
		this.timeoutTimer.next(1);
	}

	public revertAuthenticationCheck(): void {
		if (AuthUtil.isOktaEnabled(this.environment.cloud)) {
			this.oktaAuthenticationService.isAuthenticated$.next(false);
		} else {
			this.authService.checkAuthorisation.next(false);
		}
	}

	/*************************************************
	 * Private methods
	 *************************************************/


	/**
	 * Keep alive is triggered every KEEP_ALIVE_MINUTES minutes based on mouse movement
	 * An API call is made to refresh the token since the user is active on the page.
	 * This API call then triggers the init() above through the interceptor which resets the timers.
	 */
	private setupKeepAlive(): void {
		if (this.keepAliveSubscription) {
			this.keepAliveSubscription.unsubscribe();
		}

		let keepAliveTimeMs = this.KEEP_ALIVE_MINUTES * 60 * 1000;
		const isCwpUserSignedIn = get(window, 'BLK.isCwpUserSignedIn', false);
		if (AuthUtil.isOktaEnabled(this.environment.cloud)) {
			// Update keep alive - request a new token 
			const oktaExpirySecs = this.oktaAuthenticationService.atmosAuth.getExpiry();
			const currentTimeSecs = Math.floor(+new Date() / 1000);
			const expiryBufferSecs = 60;
			keepAliveTimeMs = (oktaExpirySecs - currentTimeSecs - expiryBufferSecs) * 1000;
		}

		this.keepAliveSubscription = fromEvent(window, 'mousemove')
			.pipe(
				auditTime(keepAliveTimeMs), // ignore until keep alive minutes
				takeUntil(this.timeoutTimer), // do not trigger after timeout timer
				switchMap(() => AuthUtil.isOktaEnabled(this.environment.cloud) ? this.oktaAuthenticationService.refreshTokens() : this.authService.isAuthorised())
			)
			.subscribe(
				authenticated => {
					if (!authenticated && isCwpUserSignedIn) {
						this.forceTimeout();
					}
				},
				err => {
					console.error('Keep alive failed', err);
					this.forceTimeout();
				}
			);
	}

	private resetTimer(): void {
		this.stopTimer();
		this.startTimer();
	}

	private startTimer(): void {
		this.timeoutTimer?.complete();
		this.timerSubscription?.unsubscribe();

		this.timeoutTimer = new Subject();

		// Possiblity to override timeout minutes via DCR if required
		const timeoutMinutes = Number(this.contentService.get('OKTA_TIMEOUT_MINS', String(this.OKTA_TIMEOUT_MINUTES)));
		const timeoutMilliseconds = timeoutMinutes * 60 * 1000;
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		timer(timeoutMilliseconds).subscribe(this.timeoutTimer);

		this.timerSubscription = this.timeoutTimer.pipe(first()).subscribe(() => {
			this.dispatchSessionTimeoutAnalyticEvent();
			this.openSessionTimeoutDialog();
			this.stopTimer();
		});
	}

	private stopTimer(): void {
		this.timerSubscription?.unsubscribe();
	}

	private openSessionTimeoutDialog(): void {

		if (this.hasSessionTimeoutDialogOpened()) {
			return;
		}

		let actionArray = [];
		const isCwpUserSignedIn = get(window, 'BLK.isCwpUserSignedIn', false);

		if (this.isPublicApp && isCwpUserSignedIn) {
			const redirectUrl = this.hrefResolver.getResolvedUrl('okta-sign-on') + '?redirectUrl=' + window.location.pathname + '?signInAppWithOkta=true';
			actionArray = [{ label: 'Sign In', url: redirectUrl }];
		} else {
			actionArray = [{ label: 'Sign In', reload: true }];
		}

		this.matDialog.closeAll();
		this.matDialog.open(MessageDialogComponent, {
			width: '600px',
			disableClose: true,
			data: {
				hasCloseIcon: false,
				isSessionTimeoutDialog: true,
				message: {
					title: 'Session Expired',
					trackMsgPrefix: ERROR_SEVERITY.WARNING + ':',
					description: 'Your session is expired, please sign in again to continue',
					actions: actionArray
				}
			}
		});
	}

	private dispatchSessionTimeoutAnalyticEvent(): void {
		const eventName = AnalyticEventEvaluatorUtil.transform('Session Expired');
		const tealiumEvent = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.GENERIC, { linkName: eventName });
		this.tealiumAnalyticsService.trackEventTealium(tealiumEvent);
	}

	private hasSessionTimeoutDialogOpened(): boolean {
		const dialogs = this.matDialog?.openDialogs ?? [];
		return dialogs.some(dialog => dialog.componentInstance?.data?.isSessionTimeoutDialog);
	}
}
